import React, { useMemo, useState } from "react"
import { FileCheck, FileText, Loader2, Receipt, Save, Scroll } from "lucide-react"
import { Button } from "src/components/ui/button"
import { Input } from "src/components/ui/input"
import { Label } from "src/components/ui/label"
import { Textarea } from "src/components/ui/textarea"
import { Tabs, TabsList, TabsTrigger } from "src/components/ui/tabs"
import { cn } from "src/utils/cn"
import { useToast } from "src/components/ui/toast"
import { useAuth } from "src/contexts/AuthContext"
import { useDocumentSettings } from "../hooks/useDocumentSettings"
import DocumentPreview from "./DocumentPreview"

const DOC_TABS = [
  { id: "booking", label: "Booking Form", icon: FileCheck },
  { id: "quotation", label: "Cost Sheet", icon: FileText },
  { id: "demand_letter", label: "Demand Letter", icon: Scroll },
  { id: "receipt", label: "Receipt", icon: Receipt },
]

const TEXT_FIELDS = [
  { key: "header", label: "Header line", hint: "Small italic line under the company name." },
  { key: "title", label: "Document title", hint: "Bold centered title (e.g. QUOTATION)." },
  { key: "signatory_name", label: "Signatory name" },
  { key: "signatory_designation", label: "Signatory designation" },
  { key: "footer", label: "Footer note", hint: "Fine print at the bottom of every page." },
]

const AREA_FIELDS = [
  { key: "body", label: "Intro / body text", rows: 3 },
  { key: "terms", label: "Terms & conditions", rows: 4 },
]

const BRANDING_FIELDS = [
  { key: "address", label: "Address" },
  { key: "phone", label: "Phone" },
  { key: "email", label: "Email" },
  { key: "website", label: "Website" },
  { key: "gst_number", label: "GSTIN" },
]

export default function DocumentSettingsForm() {
  const { toast } = useToast()
  const { organization } = useAuth()
  const { config, setConfig, loading, saving, save } = useDocumentSettings()
  const [activeTab, setActiveTab] = useState("booking")

  const orgName = organization?.name || "Your Company"

  const branding = config?.branding || {}
  const docConfig = useMemo(() => (config ? config[activeTab] || {} : {}), [config, activeTab])

  function setDocField(key, value) {
    setConfig((prev) => ({ ...prev, [activeTab]: { ...(prev[activeTab] || {}), [key]: value } }))
  }

  function setBrandingField(key, value) {
    setConfig((prev) => ({ ...prev, branding: { ...(prev.branding || {}), [key]: value } }))
  }

  async function handleSave() {
    try {
      await save({ branding: config.branding, [activeTab]: config[activeTab] })
      toast({ title: "Saved", description: "Document settings updated." })
    } catch (errors) {
      toast({ variant: "destructive", title: "Error", description: errors?.[0] || "Failed to save." })
    }
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 p-8 text-sm text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading settings…
      </div>
    )
  }
  if (!config) return null

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-[18px] font-semibold text-slate-900">Document Templates</h1>
          <p className="mt-0.5 text-[13px] text-slate-500">
            Configure the branding and copy used on generated booking confirmations, cost sheets, demand letters and receipts.
          </p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="gap-1.5">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          {saving ? "Saving…" : "Save changes"}
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex justify-start gap-1 rounded-none border-b border-slate-200 bg-transparent p-0">
          {DOC_TABS.map((tab) => {
            const Icon = tab.icon
            const active = activeTab === tab.id
            return (
              <TabsTrigger
                key={tab.id}
                value={tab.id}
                className={cn(
                  "-mb-px flex items-center gap-1.5 rounded-none border-b-2 bg-transparent px-3 py-2 text-[13px] font-medium shadow-none transition-colors",
                  active
                    ? "border-slate-900 text-slate-900"
                    : "border-transparent text-slate-500 hover:text-slate-700"
                )}
              >
                <Icon className="h-3.5 w-3.5" />
                {tab.label}
              </TabsTrigger>
            )
          })}
        </TabsList>
      </Tabs>

      {/* Side-by-side: config left, live preview right */}
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        {/* Config */}
        <div className="flex flex-col gap-6">
          <section className="rounded-lg border border-slate-200 bg-white p-5">
            <h2 className="text-[14px] font-semibold text-slate-900">{DOC_TABS.find((t) => t.id === activeTab)?.label} content</h2>
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {TEXT_FIELDS.map((f) => (
                <div key={f.key} className={f.key === "footer" ? "sm:col-span-2" : ""}>
                  <Label className="text-[12px] text-slate-600">{f.label}</Label>
                  <Input
                    value={docConfig[f.key] || ""}
                    onChange={(e) => setDocField(f.key, e.target.value)}
                    className="mt-1 text-[13px]"
                  />
                  {f.hint && <p className="mt-1 text-[11px] text-slate-400">{f.hint}</p>}
                </div>
              ))}
            </div>
            <div className="mt-4 flex flex-col gap-4">
              {AREA_FIELDS.map((f) => (
                <div key={f.key}>
                  <Label className="text-[12px] text-slate-600">{f.label}</Label>
                  <Textarea
                    rows={f.rows}
                    value={docConfig[f.key] || ""}
                    onChange={(e) => setDocField(f.key, e.target.value)}
                    className="mt-1 text-[13px]"
                  />
                </div>
              ))}
            </div>
          </section>

          <section className="rounded-lg border border-slate-200 bg-white p-5">
            <h2 className="text-[14px] font-semibold text-slate-900">Branding</h2>
            <p className="mt-0.5 text-[12px] text-slate-500">Shared across all document types.</p>
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {BRANDING_FIELDS.map((f) => (
                <div key={f.key}>
                  <Label className="text-[12px] text-slate-600">{f.label}</Label>
                  <Input
                    value={branding[f.key] || ""}
                    onChange={(e) => setBrandingField(f.key, e.target.value)}
                    className="mt-1 text-[13px]"
                  />
                </div>
              ))}
              <div>
                <Label className="text-[12px] text-slate-600">Accent color</Label>
                <div className="mt-1 flex items-center gap-2">
                  <input
                    type="color"
                    value={`#${(branding.accent_color || "111827").replace("#", "")}`}
                    onChange={(e) => setBrandingField("accent_color", e.target.value.replace("#", ""))}
                    className="h-9 w-12 cursor-pointer rounded border border-slate-200 bg-white p-1"
                  />
                  <Input
                    value={branding.accent_color || ""}
                    onChange={(e) => setBrandingField("accent_color", e.target.value.replace("#", ""))}
                    className="text-[13px]"
                  />
                </div>
              </div>
            </div>
          </section>
        </div>

        {/* Live preview */}
        <div>
          <div className="sticky top-6">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-[12px] font-medium uppercase tracking-wide text-slate-400">Live preview</span>
            </div>
            <div className="max-h-[calc(100vh-160px)] overflow-y-auto rounded-xl bg-slate-100 p-6">
              <DocumentPreview type={activeTab} config={docConfig} branding={branding} orgName={orgName} />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
